import getUrlDict from './getUrlDict'
import { env } from './env'
import type { Tag } from '../api/models/types'

export const TEST_MODE = testModeIsOn()

function testModeIsOn() {
  if (getUrlDict().savvy_test === 'false') return false
  if (getUrlDict().savvy_test) return true
  if (localStorageGet('savvy_test') === 'false') return false
  if (localStorageGet('savvy_test')) return true
  return testModeIsDefaultOn()
}

function testModeIsDefaultOn() {
  return env() !== 'production'
}

// eslint-disable-next-line no-undef
export const STRIPE_TEST_PKEY = process.env.STRIPE_TEST_PKEY || 'pk_test_Vqfo5zJGT1MKkBrAY20IZSjy'

export const IP_DATA_API_KEY =
  // eslint-disable-next-line no-undef
  process.env.IP_DATA_API_KEY || 'bd6fe64d602371f3257a783b2869e7794056f2418fc88a00b14da5a9'
export const IPAPI_API_KEY =
  // eslint-disable-next-line no-undef
  process.env.IPAPI_API_KEY || 'R005IIGJnyBNFyv'

export function getUrlParam(key: string) {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get(key)
}

export function hasUrlParam(key: string, value?: string, url?: URL) {
  const urlParams = new URLSearchParams(url?.search || window.location.search)
  return value === undefined
    ? ![undefined, null, false, 'false'].includes(urlParams.get(key))
    : urlParams.get(key) === value
}

export function getHashParams() {
  const params: any = {}
  const hash = window.location.hash
  if (hash) {
    const hashParams = new URLSearchParams(hash)
    for (let [key, value] of hashParams) {
      // if the key starts with a # in it, remove it
      const newKey = key.startsWith('#') ? key.slice(1) : key
      params[newKey] = value
    }
  }

  return params
}

export function removeHashParameters() {
  /**
   * Redirect to the page without the hash params
   * 1. Get the current URL
   * 2. Remove the hash params
   * 3. Redirect to the new URL
   */
  const currentUrl = window.location.href
  const newUrl = currentUrl.replace(/#.*$/, '')

  window.history.pushState('', '', newUrl)
}

export function hasEnvProp(propKey: string, value?: string) {
  const allKeys: { [key: string]: string[] } = {
    page: ['savvy_page', 'current_page_key'],
    page_id: ['savvy_page_id', 'current_page_id'],
    page_index: ['savvy_page_index', 'current_page_index'],
    debug: ['savvy_debug'],
    test: ['savvy_test'],
    editing: ['savvy_editor', 'savvy_editing'],
    version: ['savvy_version'],
    commenting: ['savvy_commenting', 'savvy_comments', 'savvy_comment'],
  }

  const keys: string[] = allKeys[propKey].concat(env() !== 'production' ? [propKey] : [])

  const res = keys.some((key) => hasUrlParam(key, value))

  return res
}

export function hasEnvProps(propKeys: string[], value?: string) {
  return propKeys.some((key) => hasEnvProp(key, value))
}

export function getComponentElement(self: any, key: string) {
  const flow = self.element.closest(`[data-controller*="flow"]`)
  const selector = `.ComponentKey-${key}`
  return flow ? flow.querySelector(selector) : self.element.closest(selector)
}

export function addDomElementToPage(
  location: 'head' | 'body',
  type: string,
  attrs?: { styles?: { [key: string]: string }; [key: string]: any },
  parent?: HTMLElement,
  onlyIfNotExists?: boolean,
  // For 'waitForLoad', we can either:
  // 1. Set to true to use onload.
  // 2. set to a string to wait for a window function to exist.
  // If you use #2, then this can combine with 'onlyIfNotExists' to wait for an existing script to load.
  waitForLoad?: boolean | string
): HTMLElement | Promise<HTMLElement> {
  const eventName = `script_loaded_for_${waitForLoad}`
  const attrsString = Object.entries(attrs || {})
    .map(([key, value]) => `[${key}="${value}"]`)
    .join('')

  const existingElement = (document.querySelector(`${type}${attrsString}`) ||
    null) as HTMLElement | null

  if (existingElement && onlyIfNotExists) {
    // @ts-ignore
    if (typeof waitForLoad === 'string' && !window[waitForLoad]) {
      return new Promise((resolve) => {
        existingElement.addEventListener(eventName, () => {
          return resolve(existingElement as HTMLElement)
        })
      })
    } else if (typeof waitForLoad === 'string' && window[waitForLoad as keyof Window]) {
      return new Promise((resolve) => {
        return resolve(existingElement as HTMLElement)
      })
    } else {
      return existingElement
    }
  } else {
    const newElement = document.createElement(type) as HTMLElement
    if (attrs) {
      if (attrs.styles) {
        Object.keys(attrs.styles).forEach((key) => {
          if (attrs.styles) newElement.style[key as unknown as number] = attrs.styles[key]
        })
      }
      Object.entries(attrs).forEach(([key, value]) => {
        if (typeof value === 'string') newElement.setAttribute(key, value as string)
      })
    }
    ;(parent || document[location]).appendChild(newElement)

    if (waitForLoad) {
      return new Promise((resolve) => {
        newElement.onload = () => {
          if (typeof waitForLoad === 'string') window.dispatchEvent(new CustomEvent(eventName, {}))
          return resolve(newElement as HTMLElement)
        }
      })
    } else {
      return newElement
    }
  }
}

export function addDomElementToHead(
  type: string,
  attrs?: { styles?: { [key: string]: string }; [key: string]: any },
  onlyIfNotExists?: boolean,
  waitForLoad?: boolean | string
) {
  return addDomElementToPage('head', type, attrs, undefined, onlyIfNotExists, waitForLoad)
}

export function addDomElementToBody(
  type: string,
  attrs?: { styles?: { [key: string]: string }; [key: string]: any },
  parent?: HTMLElement,
  onlyIfNotExists?: boolean,
  waitForLoad?: boolean | string
) {
  return addDomElementToPage('body', type, attrs, parent, onlyIfNotExists, waitForLoad)
}

export function initAndWaitForSupabaseClient() {
  try {
    if (window.supabaseClient) return Promise.resolve(window.supabaseClient)
    return (
      addDomElementToHead(
        'script',
        {
          src: 'https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2',
          type: 'text/javascript',
          async: true,
          defer: true,
        },
        true,
        'supabase'
      ) as Promise<HTMLElement>
    ).then(() => window.supabase.createClient(SUPABASE_URL, SUPABASE_ANON_KEY))
  } catch (error) {
    console.error(
      `Error initializing Supabase client - addDomElementToHead wasn't a promise.`,
      error
    )
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(window.supabase.createClient(SUPABASE_URL, SUPABASE_ANON_KEY))
      }, 2000)
    })
  }
}

export function injectHighlight(optionalHighlightProjectId?: string) {
  try {
    const src = 'https://cdn.jsdelivr.net/npm/highlight.run@8.11.0'

    if (document.querySelector(`script[src="${src}"]`)) return

    const scriptEl = addDomElementToHead('script', {
      src,
    }) as HTMLElement
    scriptEl.onload = () => {
      const branchOrigins = window.location.hostname.endsWith('.csb.app')
        ? [window.location.hostname.replace('9000', '8787')]
        : []
      window.H.init(optionalHighlightProjectId || '2d1q57er', {
        environment: env(),
        sessionShortcut: 'cmd+option+h,ctrl+alt+h',
        tracingOrigins: [
          'queues.trysavvy.com',
          'renderer.trysavvy.com',
          'renderer-staging.trysavvy.com',
          'renderer-ready-for-qa.trysavvy.com',
          'renderer-side-staging.trysavvy.com',
          'renderer-side-staging-2.trysavvy.com',
          'renderer-side-staging-3.trysavvy.com',
          ...branchOrigins,
        ],
        networkRecording: {
          enabled: true,
          recordHeadersAndBody: true,
        },
      })
    }
  } catch (error) {
    console.warn('Error injecting Highlight', error)
  }
}

export function injectPostHog(projectId: string) {
  try {
    const postHogInitCode = `
      !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.async=!0,p.src=s.api_host+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="capture identify alias people.set people.set_once set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
    `
    eval(postHogInitCode)
    ;(window as any).posthog.init(projectId, {
      api_host: 'https://app.posthog.com',
    })
  } catch (error) {
    console.warn('Error injecting PostHog', error)
  }
}

export function initCommandBar() {
  const scriptEl = addDomElementToHead('script', {}) as HTMLElement

  // index.html
  const scriptString = `	
    (function(){var o="7c13eb50",n=["Object.assign","Symbol","Symbol.for"].join("%2C"),a=window;function t(o,n){void 0===n&&(n=!1),"complete"!==document.readyState&&window.addEventListener("load",t.bind(null,o,n),{capture:!1,once:!0});var a=document.createElement("script");a.type="text/javascript",a.async=n,a.src=o,document.head.appendChild(a)}function r(){var n;if(void 0===a.CommandBar){delete a.__CommandBarBootstrap__;var r=Symbol.for("CommandBar::configuration"),e=Symbol.for("CommandBar::orgConfig"),c=Symbol.for("CommandBar::disposed"),i=Symbol.for("CommandBar::isProxy"),m=Symbol.for("CommandBar::queue"),l=Symbol.for("CommandBar::unwrap"),d=[],s=localStorage.getItem("commandbar.lc"),u=s&&s.includes("local")?"http://localhost:8000":"https://api.commandbar.com",f=Object.assign(((n={})[r]={uuid:o},n[e]={},n[c]=!1,n[i]=!0,n[m]=new Array,n[l]=function(){return f},n),a.CommandBar),p=['addEventSubscriber', 'addEventHandler', 'addRecordAction', 'setFormFactor'],y=f;Object.assign(f,{shareCallbacks:function(){return{}},shareContext:function(){return{}}}),a.CommandBar=new Proxy(f,{get:function(o,n){return n in y?f[n]:p.includes(n)?function(){var o=Array.prototype.slice.call(arguments);return new Promise((function(a,t){o.unshift(n,a,t),f[m].push(o)}))}:function(){var o=Array.prototype.slice.call(arguments);o.unshift(n),f[m].push(o)}}}),null!==s&&d.push("lc=".concat(s)),d.push("version=2"),t("".concat(u,"/latest/").concat(o,"?").concat(d.join("&")),!0)}}void 0===Object.assign||"undefined"==typeof Symbol||void 0===Symbol.for?(a.__CommandBarBootstrap__=r,t("https://polyfill.io/v3/polyfill.min.js?version=3.101.0&callback=__CommandBarBootstrap__&features="+n)):r();})();	
    window.CommandBar.boot();	
  `

  scriptEl.innerHTML = scriptString
}

export async function createAndInsertCustomFontTag({ tag }: { tag: Tag }) {
  const tagMetadata = tag?.metadata
  const url: string = tag?.url || ''
  if (!url) return

  //* Handle custom fonts (requires preload link + @font-face style)

  addDomElementToHead('link', {
    rel: 'preload',
    as: 'font',
    href: url,
    crossorigin: 'anonymous',
  })

  //* Create @font-face style tag
  const family: string = tagMetadata?.name || 'CustomFont'
  const display: string = 'swap' //* Default font-display value

  //* Extract format from URL - handle query params by checking pathname
  let format: string = 'woff2' //* Default format

  const urlObj = new URL(url)
  const pathname = urlObj.pathname.toLowerCase()
  if (pathname.endsWith('.woff2')) {
    format = 'woff2'
  } else if (pathname.endsWith('.woff')) {
    format = 'woff'
  } else if (pathname.endsWith('.ttf')) {
    format = 'truetype'
  } else if (pathname.endsWith('.otf')) {
    format = 'opentype'
  }

  const fontFaceCss = `@font-face {
  font-family: '${family}';
  src: url('${url}') format('${format}');
  font-display: ${display};
}`

  const styleEl = await addDomElementToHead('style')
  styleEl.textContent = fontFaceCss
}

export function trackHighlightEvent(eventName: string, data?: any) {
  if (!window.H) return

  try {
    window.H.track(eventName, data)
  } catch (error) {
    console.warn(
      `Error tracking Highlight event ${eventName} with data:`,
      data,
      `\n\n Error:`,
      error
    )
  }
}

export function trackPostHogEvent(eventName: string, data?: any) {
  return
  if (!window.posthog) return setTimeout(() => track(eventName, data), 1000)

  track(eventName, data)

  function track(eventName: string, data?: any) {
    if (!window.posthog) return

    try {
      window.posthog.capture(eventName, data)
    } catch (error) {
      console.warn(
        `Error tracking PostHog event ${eventName} with data:`,
        data,
        `\n\n Error:`,
        error
      )
    }
  }
}

export function identifyPostHogUser(data: any) {
  return
  if (!window.posthog) return setTimeout(() => identify(data), 1000)

  identify(data)

  function identify(data: any) {
    if (!window.posthog) return

    try {
      if (!data.id) return
      window.posthog.identify(data.user_id || data.id, data)
    } catch (error) {
      console.warn(`Error identifying PostHog user with data:`, data, `\n\n Error:`, error)
    }
  }
}

export function identifyJuneUser(data: any, group: any) {
  window.analytics.identify(data.id, data)
  window.analytics.group(group.id, group)
}

export function getWithIds(
  json: any,
  path: (string | number)[],
  createIfNeeded?: boolean,
  alternativeObjectMatchKeys?: string[],
  fullPath?: (string | number)[]
): any {
  //* If the path is empty, return the json
  if (path.length === 0) return json

  //* Divide the path into the current step and the rest of the path
  let currentStep = path[0]
  const restOfPath = path.slice(1)
  const isLastStep = path.length === 1
  fullPath = fullPath ? fullPath : path

  //* If the json of the parent of the currenStep is an array, and the current step is defined by a string I want to traslate to an index
  if (Array.isArray(json) && typeof currentStep === 'string') {
    //* By default try to find the index by id
    let index = json.findIndex((item) => item.id === currentStep)

    //* If not found by id and alternative keys are provided, try those
    if (index === -1 && alternativeObjectMatchKeys && alternativeObjectMatchKeys.length > 0) {
      index = json.findIndex((item) =>
        alternativeObjectMatchKeys.some((key) => item[key] === currentStep)
      )
    }

    // currentStep = index === -1 ? currentStep : index

    //* If the index is not found, throw an error
    if (index === -1) {
      throw new Error(`Could not find ${currentStep} in ${fullPath.slice(0, -1).join(' / ')}`)
    }

    //* If the index is found, set the current step to the index
    currentStep = index
  }

  //* Get the json of the current step
  let nextJson = json[currentStep]

  if (!nextJson) {
    if (createIfNeeded) {
      json[currentStep] = typeof restOfPath[0] === 'number' ? [] : {}
      nextJson = json[currentStep]
    } else {
      if (!isLastStep) {
        throw new Error(`Could not find ${currentStep} in ${fullPath.slice(0, -1).join(' / ')}`)
      } else {
        throw new Error(`no-last-step`)
      }
    }
  }

  return getWithIds(nextJson, restOfPath, createIfNeeded, alternativeObjectMatchKeys, fullPath)
}

export function generateId(prefix: string) {
  const randomString = Math.random().toString(20).slice(2) + Math.random().toString(20).slice(2)

  return prefix ? `${prefix}_${randomString}` : `_${randomString}`
}

export const SUPABASE_URL = process.env.SUPABASE_URL || 'https://ierxexdtyashuotcsjyo.supabase.co'
export const SUPABASE_ANON_KEY =
  process.env.SUPABASE_ANON_KEY ||
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImllcnhleGR0eWFzaHVvdGNzanlvIiwicm9sZSI6ImFub24iLCJpYXQiOjE2NjQ4ODU2NDIsImV4cCI6MTk4MDQ2MTY0Mn0.AbUQz18lps79WZBW1kmbFP2lUTiAF6ttTuFK14yvUuw'

export function localStorageGet(key: string) {
  try {
    return localStorage.getItem(key)
  } catch (error) {
    console.warn(`Wasn't able to get ${key} from localStorage`)
    return undefined
  }
}

export function localStorageSet(key: string, val: any) {
  try {
    return localStorage.setItem(key, val)
  } catch (error) {
    console.warn(`Wasn't able to set ${key} in localStorage`, error)
    return false
  }
}

export function sessionStorageGet(key: string) {
  try {
    return sessionStorage.getItem(key)
  } catch (error) {
    console.warn(`Wasn't able to get ${key} from sessionStorage`, error)
    return undefined
  }
}

export function sessionStorageSet(key: string, val: any) {
  try {
    return sessionStorage.setItem(key, val)
  } catch (error) {
    console.warn(`Wasn't able to set ${key} in sessionStorage`, error)
    return undefined
  }
}

export function sessionStorageRemove(key: string) {
  try {
    return sessionStorage.removeItem(key)
  } catch (error) {
    console.warn(`Wasn't able to remove ${key} from sessionStorage`, error)
    return undefined
  }
}

export function alertUser(
  type: string,
  title: string,
  description?: string,
  inputs?: { key: string; placeholder: string }[],
  buttons?: { text: string; action?: (...args: any) => void }[]
) {
  var event = new CustomEvent('savvy:new_alert', {
    detail: {
      type,
      title,
      description,
      inputs,
      buttons,
    },
  })
  window.dispatchEvent(event)
}

export function isBuilderEditingMode(url?: URL) {
  if (!url) url = new URL(window.location.href)
  return hasUrlParam('savvy_editing', undefined, url)
}

//* Check if the lottie script is already in the dom and if not add it to it
interface LottieScriptResult {
  status: 'already added' | 'added'
}
export function setLottieScript(): Promise<LottieScriptResult> {
  return new Promise((resolve) => {
    const lottieScriptUrl = `https://unpkg.com/@lottiefiles/lottie-player@2.0.4/dist/lottie-player.js`
    const lottieScript = document.getElementById('lottie-script')
    const lottieScriptFound = !!lottieScript

    if (lottieScriptFound) return resolve({ status: 'already added' })

    //* If the script has not been added to the DOM, add it and then re add the lottie-player element
    if (!lottieScriptFound) {
      const script = document.createElement('script')
      script.src = lottieScriptUrl
      script.id = 'lottie-script'
      document.head.appendChild(script)
      script.onload = () => {
        resolve({ status: 'added' })
      }
    }
  })
}

export function collectGarbage() {
  window.StimulusApp.dispatcher.eventListenerMaps.forEach(
    (elm: Map<HTMLElement, { unorderedBindings: Set<number> }>, index: number) => {
      Array.from(elm.values()).forEach((v: { unorderedBindings: Set<number> }, index2: number) => {
        //* If the number of unordered bindings is 0, we can safely delete the element
        if (v.unorderedBindings.size === 0) {
          const key = elm.keys().next().value
          if (key) elm.delete(key)
          //* If the element is empty, we can delete the whole map
          if (elm.size === 0) window.StimulusApp.dispatcher.eventListenerMaps.delete(index)
        }
      })
    }
  )
}

export function getRendererBase() {
  switch (env()) {
    case 'local':
      return 'http://localhost:8787'
    case 'ci':
      return 'http://localhost:8787'
    // return `https://${window.location.hostname}/api`
    case 'branch':
      return window.location.origin.replace('9000', '8787')
    case 'dev':
    case 'preview-dev':
      return 'https://engine-dev.embeddables.com'
    case 'staging':
    case 'preview-staging':
      return 'https://engine-staging.embeddables.com'
    case 'preview-ready-for-qa':
      return 'https://engine-ready-for-qa.embeddables.com'
    case 'side-staging':
    case 'preview-side-staging':
      return 'https://engine-side-staging.embeddables.com'
    case 'side-staging-2':
    case 'preview-side-staging-2':
      return 'https://engine-side-staging-2.embeddables.com'
    case 'preview-side-staging-3':
      return 'https://engine-side-staging-3.embeddables.com'
    case 'preview':
    case 'production':
      return 'https://engine.embeddables.com'
    default:
      throw new Error('Unknown Environment for Renderer')
  }
}

 //* Deeply freezes an object and all its nested properties to protect against mutations.
 //* This is useful for preventing accidental modifications to objects passed to callbacks.
export function deepFreeze<T>(obj: T): T {
  if (!obj || typeof obj !== 'object') return obj
  Object.freeze(obj)
  for (const key of Object.keys(obj as any)) {
    const val = (obj as any)[key]
    if (val && typeof val === 'object' && !Object.isFrozen(val)) {
      deepFreeze(val)
    }
  }
  return obj
}
